import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSepaTransactions } from "../../../../../stores/Sepa/sepa.queries";
import { SepaTransactionRow } from "../../../../../stores/Sepa/sepa.types";
import { useDownload } from "../../../../../hooks/useDownload";
import SepaTransactionTable from "./SepaTransactionTable";
import CursorLoadMore from "./CursorLoadMore";
import { SepaIdleBlock } from "./SepaListStates";

interface Props {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when a hit's export chip is clicked (e.g. switch to the Exporte tab). */
  onOpenBatch?: (batch: { id: string; batchNumber: number }) => void;
}

/**
 * Global "spotlight" search over an account's SEPA transactions. Unlike the tabs,
 * it ignores direction/status so a hit is found regardless of where it lives, and
 * each result surfaces its export batch (if already exported). The search is only
 * fired on submit (Enter or button) — no debounce — to keep it light.
 */
const SepaSpotlightSearch: React.FC<Props> = ({ accountId, open, onOpenChange, onOpenBatch }) => {
  const [inputValue, setInputValue] = useState("");
  const [submitted, setSubmitted] = useState("");
  const { handleDownloadPdf } = useDownload();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Reset when the dialog closes so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setInputValue("");
      setSubmitted("");
    }
  }, [open]);

  const query = useSepaTransactions(
    { accountId, searchStr: submitted, limit: 25 },
    open && submitted.length > 0,
  );

  const rows = useMemo<SepaTransactionRow[]>(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(inputValue.trim());
  };

  const openBatch = (batch: { id: string; batchNumber: number }) => {
    onOpenBatch?.(batch);
    onOpenChange(false);
  };

  const hasQuery = submitted.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] gap-0 overflow-hidden p-0 sm:max-w-7xl">
        <DialogHeader className="border-b border-solid border-slate-200 px-7 py-5">
          <DialogTitle className="text-lg font-semibold text-slate-800">
            Transaktion suchen
          </DialogTitle>
          <DialogDescription className="text-[15px] text-slate-500">
            Sucht über alle Lastschriften und Rückerstattungen dieses Mandanten (Transaktions-Nr.,
            Patient oder Rezeptnr.).
          </DialogDescription>
          <form onSubmit={handleSubmit} className="mt-3 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                autoFocus
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Suchbegriff eingeben und Enter drücken…"
                className="h-11 pl-9 text-base"
              />
            </div>
            <Button
              type="submit"
              className="h-11 bg-[#4189a8] text-base font-semibold text-white hover:bg-[#367b9a]"
              disabled={inputValue.trim().length === 0}
            >
              Suchen
            </Button>
          </form>
        </DialogHeader>

        <div className="thin-scrollbar max-h-[65vh] overflow-y-auto px-4 py-3" ref={scrollRef}>
          {!hasQuery ? (
            <SepaIdleBlock message="Suchbegriff eingeben und Enter drücken." />
          ) : (
            <>
              <SepaTransactionTable
                rows={rows}
                showExportColumn
                hideActions
                onOpenBatch={openBatch}
                onDownloadDocument={handleDownloadPdf}
                isLoading={query.isLoading}
                isError={query.isError}
                error={query.error}
                emptyText={`Keine Treffer für „${submitted}".`}
                bare
              />
              <CursorLoadMore
                hasNextPage={!!query.hasNextPage}
                isFetchingNextPage={query.isFetchingNextPage}
                onLoadMore={() => query.fetchNextPage()}
                scrollRootRef={scrollRef}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SepaSpotlightSearch;
